import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { IQueryContextDatasourceColumn } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import { ICreatedInstanceId, IGcpProjectId } from 'src/app/utils/IFactory';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildWhereSQL } from 'src/app/utils/query/sql';
import {
    DataLoaderResult,
    IDataLoaderResult,
} from '../../../services/data-loader/data-loader-result';
import { standardRun } from '../../../services/data-loader/utils/standard-run';

export class DatasourceColumnOldDataLoaderFactoryOptions
    implements ICreatedInstanceId, IGcpProjectId
{
    public createdInstanceId: string;
    public gcpProjectId: GcpProjectId;
    public queryCacheService: QueryCacheService;
    public datasourceColumnId: DatasourceColumnId;
    public datasourceSqlMap: IDatasourceSQLMap;
    /** Add where statement based on queryContext */
    filterContext: boolean | DatasourceColumnId[];
    public ignoredContextColumnIds: DatasourceColumnId[];
    public orderByQuery: string | undefined;

    constructor(options: {
        createdInstanceId: string;
        gcpProjectId: GcpProjectId;
        queryCacheService: QueryCacheService;
        datasourceColumnId: DatasourceColumnId;
        datasourceSqlMap: IDatasourceSQLMap;
        /** Add where statement based on queryContext. Default false */
        filterContext?: boolean;
        /** Ignore columns from queryContext */
        ignoredContextColumnIds?: DatasourceColumnId[];
        orderByQuery?: string;
    }) {
        this.createdInstanceId = options.createdInstanceId;
        this.gcpProjectId = options.gcpProjectId;
        this.queryCacheService = options.queryCacheService;
        this.datasourceColumnId = options.datasourceColumnId;
        this.datasourceSqlMap = options.datasourceSqlMap;
        this.filterContext = options.filterContext ?? false;
        this.ignoredContextColumnIds = options.ignoredContextColumnIds ?? [
            'ACTIVITY_APP_VERSION',
        ];
        this.orderByQuery = options.orderByQuery;
    }
}

export class DataSourceColumnOldDataLoaderFactory extends Factory<
    DataLoader,
    DatasourceColumnOldDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'DataSourceColumnOldDataLoaderFactory';

    override getInstance(
        options: DatasourceColumnOldDataLoaderFactoryOptions
    ): DataLoader {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const datasourceColumnId: DatasourceColumnId =
                options.datasourceColumnId;
            const datasourceSqlMap: IDatasourceSQLMap =
                options.datasourceSqlMap;
            const ignoredContextColumnIds: DatasourceColumnId[] =
                options.ignoredContextColumnIds;
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
                    dsColumns.present
                        .filter(
                            (col: IQueryContextDatasourceColumn) =>
                                col.id != datasourceColumnId
                        )
                        .filter(
                            (col: IQueryContextDatasourceColumn) =>
                                //allow if filterContext is true and column is not in ignoredContextColumnIds
                                options.filterContext &&
                                ignoredContextColumnIds.indexOf(col.id) == -1
                        )
                        .map((dsCol) => {
                            return datasourceSqlMap.columns[dsCol.id]!
                                .compileSQLTokens!(dsCol.state, options);
                        });

                // merge tokens into single deduplicated object of type ICompiledSQLTokens
                const sqlTokens: ICompiledSQLTokens =
                    mergeSqlTokensArray(sqlTokensArray);

                const columnSelect =
                    datasourceSqlMap.columns[datasourceColumnId]!.columnSQL;
                const columnAlias = datasourceColumnId.replace('.', '_');

                const unnestDatasourceColumn =
                    sqlTokens.unnestDatasourceColumn.join('\n');

                const where: string = buildWhereSQL(sqlTokens.where);
                const join: string = sqlTokens.join.join('\n');
                const orderBy: string = options.orderByQuery ?? 'value';
                const query = `
-- DataLoaderId : ${this.instanceId}
--SELECT DISTINCT COALESCE(CAST(${columnSelect} AS STRING), '') AS raw, COALESCE(CAST(${columnSelect} AS STRING), 'N/A') AS ${columnAlias}
SELECT DISTINCT ${columnSelect} AS value, COALESCE(CAST(${columnSelect} AS STRING), 'N/A') AS label
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${unnestDatasourceColumn}
${join}
${where}
ORDER BY ${orderBy}`;
                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                const data = [...queryResponse.result];
                // console.log(cancellationToken);
                // console.log(pendingDataLoaderResultStatus);
                cancellationToken.throwIfCancellationRequested();
                let dl: IDataLoaderResult = {
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                };
                console.log({ datasourceColumnId, dl });
                this.emitter.emit(dl);
            };

            await standardRun(this, queryContext, process);
        });
    }
}
